import { useRef, useState } from 'react'
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { EmployeeRootPageDocument, EmployeeRouteMetaDocument } from 'generated/resources/graphql'
import { keyBy } from 'lodash'

import { observer } from 'mobx-react'

import type { Back, Tab } from 'modules/core/components/Card/types'
import NotFound from 'modules/core/components/NotFound'
import { useSection } from 'modules/core/components/SectionContainer'
import { getWhatsappUrl } from 'modules/core/components/WhatsAppButton'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import type { ModuleAvailabilityType } from 'modules/core/hooks/useModuleAvailability'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'
import EmployeePolicy from 'modules/core/lib/policies/EmployeePolicy'
import { userCompany } from 'modules/core/lib/userCompany'
import users, { employeeUser } from 'modules/core/store/collections/users'

import { BecomeModal } from 'modules/authentication/public'
import { companyInFreeTrial } from 'modules/billing/store/collections/subscriptions'
import contractVersions from 'modules/contracts/store/collections/contractVersions'
import { useGetOnboardingProcess } from 'modules/employee_onboarding/hooks/useGetOnboardingProcess'
import UninviteModal from 'modules/employees/components/Actions/UninviteModal'
import RegularAccessBanner from 'modules/employees/components/blocks/RegularAccessBanner'
import TerminationBanner from 'modules/employees/components/blocks/TerminationBanner'
import { RouteContextParams } from 'modules/employees/lib/RouteContextParams'
import { RouteContext } from 'modules/employees/lib/types'
import employees from 'modules/employees/store/collections/employees'
import { employeeJob } from 'modules/job_catalog/store/collections/roles'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'
import { timeoffModuleEnabledInAvailabilities } from 'modules/timeoff/lib/timeoffModuleEnabled'

import ActivateModal from '../../components/Actions/ActivateModal'
import DisableMfaModal from '../../components/Actions/DisableMfaModal'
import SetWelcomeSpaceAccessModal from '../../components/Actions/SetWelcomeSpaceAccessModal'

const EmployeeRootPage = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const {
    loadingModuleAvailabilities,
    moduleAvailabilities,
    goalsV1FeatureAvailability,
    hasPerformanceFullVisiblity,
  } = useOutletContext<RouteContextParams>()

  const [openBecome, setOpenBecome] = useState(false)
  const [openUninvite, setUninvite] = useState(false)
  const [openDisableMfa, setOpenDisableMfa] = useState(false)
  const [openSetWelcomeSpaceAccess, setWelcomeSpaceAccess] = useState(false)
  const [openActivate, setActivate] = useState(false)
  const { id } = useStrictParams<'id'>()
  const { employee: sessionOwner, company } = useCurrentSession()
  const cardEmployeeRight = useRef<HTMLElement | null>(null)
  const headerTitle = useRef<HTMLElement | null>(null)
  const employee = employees.get(Number(id))
  const section = useSection()
  // NOTE: sections of level 3 or more have their own layout so we can hide this one
  const hasOwnLayout = section.level >= 3
  const {
    loading: loadingEmployeeRootPageQuery,
    data,
    client,
  } = useQuery(EmployeeRootPageDocument, {
    variables: { id: Number(id), companyId: company.id },
    skip: !id,
  })

  const currentEmployee = data?.employees.employee

  const isManagedByMe = data?.employees.managed.nodes.some((e) => e.id === Number(id))
  const isMyProfile = sessionOwner?.id === Number(id)

  const canSeePersonalInfo = currentEmployee?._policies?.readPersonalInfo.can || false

  const features = keyBy(data?.features?.featuresConnection?.nodes, 'name')
  const canSeeCustomDimensions = features['dev_finance_show_custom_dimensions']?.enabled ?? false
  const seeOldCostCentersAssignments =
    features['see_old_cost_centers_assignments']?.enabled ?? false

  const canEditPersonalInfo = currentEmployee?.access.personalInfo?._policies?.edit.can || false
  const canSeePermissions = data?.apiCore._policies?.companyManagePermissions.can || false
  const employeeHasContract = !!currentEmployee?.referenceContract
  const permissionGroup = currentEmployee?.access.permissionGroup
  const permissionGroupName = permissionGroup?.name || ''
  const permissionGroupId = permissionGroup?.id?.toString() || ''
  const canSeePerformance = currentEmployee?._policies?.seeEmployeePerformanceTab.can || false

  const {
    loading: loadingOnboardingProcess,
    process: onboardingProcess,
    fetchOnboardingProcess,
  } = useGetOnboardingProcess()

  const { loading: loadingPolicyAuthorizations } = useAsyncData(async () => {
    if (!employee) return

    await Promise.all([
      policyAuthorizations.authorizePolicies('contracts.read', [employee]),
      policyAuthorizations.authorizePolicies('contracts.edit', [employee]),
      policyAuthorizations.authorizePolicies('contracts.promote', [employee]),
      policyAuthorizations.authorizePolicies('contracts.delete_contracts', [employee]),
      policyAuthorizations.authorizePolicies('goals.read', [employee]),
      policyAuthorizations.authorizePolicies('planning_versions.read', [employee]),
    ])
  }, [employee?.id])

  const { loading: loadingContractVersions } = useAsyncData(
    () => contractVersions.fetch({ withCachedFetchParams: true }),
    []
  )

  useEffect(() => {
    if (!employee?.id) return
    fetchOnboardingProcess({
      variables: {
        employeeId: employee.id,
      },
    })
  }, [employee?.id])

  if (!employee) {
    return <NotFound />
  }

  const hasGoals = isFeatureAvailabilityEnabled([goalsV1FeatureAvailability], Features.goals_core)
  const hasReviews = isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.reviews)
  const hasCompetencies = isModuleAvailabilityEnabled(
    moduleAvailabilities,
    AvailableModules.competencies
  )
  const user = employeeUser(employee)
  const inFreeTrial = companyInFreeTrial(userCompany(user))

  const getCanSeeTimeOffTab = (moduleAvailabilities: ModuleAvailabilityType[]) => {
    const timeoffEnabled = timeoffModuleEnabledInAvailabilities(moduleAvailabilities)
    const policy = new EmployeePolicy(employee)

    return timeoffEnabled && policy.timeOffRead
  }

  const commonTabs = (moduleAvailabilities: ModuleAvailabilityType[]) => {
    let tabs: Tab[] = []

    const canSeeTimeOffTab = getCanSeeTimeOffTab(moduleAvailabilities)

    const policy = new EmployeePolicy(employee)
    const canSeePlanningVersions = policyAuthorizations.for('planning_versions.read', employee)
    const canReadGoals = policyAuthorizations.for('goals.read', employee)
    const canSeeAssessments =
      currentEmployee?._policies?.seeEmployeeCompetenciesAssessments?.can ?? false

    if (canSeePlanningVersions) {
      tabs.push({
        name: i18n.t('time_planning.title'),
        href: navigation.employees.id.planningVersions.fullPath({ id }),
      })
    }

    if (policy.documentsRead) {
      tabs = tabs.concat([
        {
          name: i18n.t('admin_employee.documents'),
          href: navigation.employees.id.documents.list.fullPath({ id }),
        },
      ])
    }

    if (canSeeTimeOffTab) {
      tabs.push({
        name: i18n.t('time_off.title'),
        href: navigation.employees.id.timeOff.fullPath({ id }),
      })
    }

    if (canSeePerformance) {
      tabs.push({
        name: i18n.t('sidebar.performance_new'),
        href: navigation.employees.id.performance.fullPath({ id }),
      })
    }

    if (hasReviews && policy.performanceRead) {
      tabs.push({
        name: i18n.t('reviews.employee_profile_tab'),
        href: navigation.employees.id.reviews.fullPath({ id }),
      })
    }

    if (hasGoals && canReadGoals) {
      tabs.push({
        name: i18n.t('goals.index.title'),
        href: navigation.employees.id.goals.fullPath({ id }),
      })
    }

    if (policy.tasksRead) {
      tabs.push({
        name: i18n.t('tasks.header'),
        href: navigation.employees.id.tasks.fullPath({ id }),
      })
    }

    if (hasCompetencies && canSeeAssessments) {
      tabs.push({
        name: i18n.t('competencies_v2.title'),
        href: navigation.employees.id.competencies.fullPath({ id }),
      })
    }

    if (data?.employees?.employee?._policies?.seeEmployeeTrainings.can) {
      tabs.push({
        name: i18n.t('trainings.my_training.all_courses_table.training'),
        href: navigation.employees.id.training.fullPath({
          id: String(id),
        }),
      })
    }

    if (
      policy.customTablesRead ||
      policy.trainingsCustomTableRead ||
      policy.competenciesCustomTableRead ||
      policy.growthPlansCustomTableRead
    ) {
      tabs.push({
        name: i18n.t('custom_tables.header'),
        href: navigation.employees.id.customTables.fullPath({ id }),
      })
    }

    return tabs
  }

  const computeTabs = (moduleAvailabilities: ModuleAvailabilityType[]): Tab[] => {
    let tabs: Tab[] = []

    if (!employee) {
      return []
    }

    const policy = new EmployeePolicy(employee)

    const canSeeEmployeeProfile = users.current.policy.can('apiCore.companySeeEmployeeProfile')
    const canSeeContract = policyAuthorizations.for('contracts.read', employee)
    const canEditContract = policyAuthorizations.for('contracts.edit', employee)
    const canSeeContractsTab = canSeeContract && (canEditContract || employeeHasContract)

    const canSeeAttendance = policyAuthorizations.for('attendance.see', employee) ?? false

    const canSeeTimeOffTab = getCanSeeTimeOffTab(moduleAvailabilities)

    const canSeeTasksTab = policy.tasksRead

    const canSeeTimePlanningTab = policyAuthorizations.for('planning_versions.read', employee)

    const overviewEnabledByFeatures =
      [
        canSeeContractsTab,
        canSeeTimeOffTab,
        canSeeTasksTab,
        canSeeTimePlanningTab,
        canSeeAttendance,
      ].filter(Boolean).length >= 2

    const canSeeOverview = isManagedByMe || isMyProfile || overviewEnabledByFeatures

    if (canSeeOverview) {
      tabs.push({
        index: true,
        name: i18n.t('overview.title'),
        href: navigation.employees.id.fullPath({ id }),
      })
    }

    if (canSeeEmployeeProfile) {
      tabs = tabs.concat([
        {
          index: !canSeeOverview,
          name: i18n.t('profile.work_details'),
          href: !canSeeOverview
            ? navigation.employees.id.fullPath({ id })
            : navigation.employees.id.workDetails.fullPath({ id }),
        },
      ])
    }

    if (canSeePersonalInfo) {
      tabs = tabs.concat([
        {
          name: i18n.t('profile.personal_details'),
          href: navigation.employees.id.personal.fullPath({ id }),
        },
      ])
    }

    if (canSeeContractsTab) {
      tabs = tabs.concat([
        {
          name: i18n.t('admin_employee.contracts'),
          href: navigation.employees.id.contractVersions.fullPath({ id }),
        },
      ])
    }

    return [...tabs, ...commonTabs(moduleAvailabilities)]
  }

  const isLoading =
    loadingModuleAvailabilities ||
    loadingOnboardingProcess ||
    loadingEmployeeRootPageQuery ||
    loadingPolicyAuthorizations ||
    loadingContractVersions

  const back: Back = {
    href: navigation.employees.list.fullPath(),
    label: i18n.t('employees.title'),
  }

  const tabs = computeTabs(moduleAvailabilities)

  const canSeeOverview = tabs.find((tab) => tab.name === i18n.t('overview.title')) !== undefined

  const context: RouteContext = {
    back,
    canSeePersonalInfo,
    canEditPersonalInfo,
    cardEmployeeRight,
    employee,
    hasCompetencies,
    hasGoals,
    hasPerformanceFullVisiblity,
    hasReviews,
    headerTitle,
    inFreeTrial,
    loading: isLoading,
    loadingModuleAvailabilities,
    loadingOnboardingProcess,
    onboardingProcess,
    showEmployeeSelector: true,
    tabs,
    canSeePermissions,
    permissionGroupName,
    permissionGroupId,
    canSeePerformance,
    canSeeCustomDimensions,
    seeOldCostCentersAssignments,
    canSeeOverview,
  }

  return (
    <ResponsivePage.Body
      variant="fullscreen"
      loading={isLoading}
      error={null}
      showHeader={false}
      onSecondaryActionClick={(key) => {
        if (key === 'whatsapp-chat') {
          const [, whatsappUrl] = getWhatsappUrl({
            phoneNumber: employee.get('phone_number') ?? undefined,
            country: employee.get('country') ?? undefined,
          })

          track(trackingEvents.comms.openWhatsappChatWithEmployee)
          window.open(whatsappUrl, '_blank')
        }
      }}
      onOtherActionClick={(key) => {
        if (key === 'revoke-invite') {
          setUninvite(true)
        } else if (key === 'unterminate') {
          setActivate(true)
        } else if (key === 'become') {
          setOpenBecome(true)
        } else if (key === 'disable-mfa') {
          setOpenDisableMfa(true)
        } else if (key === 'terminate') {
          const legalEntity = employeeLegalEntity(employee)
          track(trackingEvents.employee.termination.terminateStart, {
            company_id: legalEntity.attributes.company_id,
            user_id: employee.id,
            user_role: employeeJob(contractVersions, employee),
            time_stamp: Date.now(),
          })
          navigate(navigation.employees.id.terminate.fullPath({ id: employee.id }))
        } else if (key === 'move-to-welcome-space') {
          setWelcomeSpaceAccess(true)
        } else {
          // eslint-disable-next-line no-console
          console.warn(`Unhandled action key: ${key}`)
        }
      }}
    >
      <Box width="full" overflowY="hidden">
        {!hasOwnLayout && (
          <Box width="full">
            <TerminationBanner employee={employee} />
            <RegularAccessBanner employee={employee} />
          </Box>
        )}
        <Box width="full" overflowY="scroll">
          <Outlet context={context} />
        </Box>
      </Box>
      {openBecome && (
        <BecomeModal
          accessId={user.id}
          firstName={user.firstName}
          onClose={() => setOpenBecome(false)}
        />
      )}
      {openUninvite && (
        <UninviteModal
          employee={employee}
          onClose={() => {
            client.refetchQueries({ include: [EmployeeRouteMetaDocument] })
            setUninvite(false)
          }}
        />
      )}
      {openDisableMfa && (
        <DisableMfaModal
          employee={employee}
          onClose={() => {
            client.refetchQueries({ include: [EmployeeRouteMetaDocument] })
            setOpenDisableMfa(false)
          }}
        />
      )}
      {openSetWelcomeSpaceAccess && (
        <SetWelcomeSpaceAccessModal
          employee={employee}
          onClose={() => {
            setWelcomeSpaceAccess(false)
            client.refetchQueries({ include: [EmployeeRouteMetaDocument] })
          }}
        />
      )}
      {openActivate && (
        <ActivateModal
          employee={employee}
          onClose={() => {
            setActivate(false)
            client.refetchQueries({ include: [EmployeeRouteMetaDocument] })
          }}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default observer(EmployeeRootPage)
